/**
 * KeyboardShortcutsModal - Display all available keyboard shortcuts
 * Feature: 051-keybindings
 *
 * Shows categorized keyboard shortcuts in a modal dialog.
 */

import { Modal } from '../composed/Modal';
import { COMMANDS, formatKeybinding, type CommandDefinition } from '@/utils/commands/commandDefinitions';

export interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * KeyboardShortcutsModal - Shows all available keyboard shortcuts
 *
 * Displays commands grouped by category with formatted keybindings.
 * Triggered by Shift+/ (?) keyboard shortcut.
 *
 * @example
 * ```tsx
 * const [showShortcuts, setShowShortcuts] = useState(false);
 *
 * <KeyboardShortcutsModal
 *   open={showShortcuts}
 *   onClose={() => setShowShortcuts(false)}
 * />
 * ```
 */
export function KeyboardShortcutsModal({ open, onClose }: KeyboardShortcutsModalProps) {
  // Group commands by category
  const commandsByCategory: Record<string, CommandDefinition[]> = {};

  Object.values(COMMANDS).forEach((cmd) => {
    const category = cmd.category || 'Other';
    if (!commandsByCategory[category]) {
      commandsByCategory[category] = [];
    }

    // Skip duplicate command IDs (e.g., nextPhoto has both ArrowRight and ArrowDown)
    const exists = commandsByCategory[category].some(existing => existing.id === cmd.id);
    if (!exists) {
      commandsByCategory[category].push(cmd);
    }
  });

  // Category display names and order
  const categoryOrder = ['viewMode', 'navigation', 'critique', 'help'];
  const categoryNames: Record<string, string> = {
    viewMode: 'View Modes',
    navigation: 'Navigation',
    critique: 'Critique',
    help: 'Help',
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Keyboard Shortcuts"
      size="lg"
      closeOnOverlayClick={true}
      closeOnEscape={true}
    >
      <div className="space-y-6">
        {categoryOrder.map((categoryKey) => {
          const commands = commandsByCategory[categoryKey];
          if (!commands || commands.length === 0) return null;

          return (
            <div key={categoryKey}>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
                {categoryNames[categoryKey] || categoryKey}
              </h3>
              <div className="space-y-2">
                {commands.map((cmd) => (
                  <div
                    key={cmd.id}
                    className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-foreground">{cmd.label}</div>
                      {cmd.description && (
                        <div className="text-sm text-muted-foreground mt-0.5">
                          {cmd.description}
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <kbd className="inline-flex items-center px-2.5 py-1.5 text-sm font-mono font-semibold text-foreground bg-muted border border-border rounded">
                        {formatKeybinding(cmd.keybinding)}
                      </kbd>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Help text */}
      <div className="mt-6 pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground text-center">
          Press <kbd className="px-1.5 py-0.5 text-xs font-mono bg-muted border border-border rounded">?</kbd> to toggle this help
        </p>
      </div>
    </Modal>
  );
}
